import './grid_tile.less';

import React, { Component, Fragment, memo } from 'react';
import PropTypes from 'prop-types';

import get from 'lodash/get';
import includes from 'lodash/includes';
import noop from 'lodash/noop';

import GridTileCaption from './grid_tile_caption.jsx';
import GridTileInnerTypeWebPreview from './grid_tile_inner_type_web_preview.jsx';
import GridTileInnerTypeText from './grid_tile_inner_type_text.jsx';
import GridTileInnerTypeFormContainer from './grid_tile_inner_type_form/form_container.jsx';
import GridTileInnerTypeIframe from './grid_tile_inner_type_iframe.jsx';
import GridTileLinkTapTarget from './grid_tile_link_tap_target.jsx';
import GridTileInnerTypeImage from '../grid/grid_tile_inner_type_image.jsx';

import Image from 'zoomforth_codesbane_viewer/component_lib/image.jsx';

import {
  VIDEO_INLINE_SETTING_NONE,
  VIDEO_INLINE_SETTING_CUSTOM
} from 'zoomforth_codesbane_viewer/const/video_inline_settings.js';

import { bindTextLinkClickTrackingEvents } from 'zoomforth_codesbane_viewer/lib/analytics/utils.js';
import { setTextLinksToOpenInNewPage } from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';


/**
* What's this?
*
* React seems to force unmount the caption component when this logic is inline
* in the main GridTile component, so we move it out but more importantly we
* memoize it. This memoization ensures that the component doesn't unmount when
* the data changes after a server save.
*/
const CaptionComponent = ({ GridTileCaptionComponent, ...props })=> {
  if (!!GridTileCaptionComponent) {
    return <GridTileCaptionComponent {...props} />;
  }

  return <GridTileCaption {...props} />;
};

const MemoizedCaptionComponent = memo(CaptionComponent);


class GridTile extends Component {

  constructor(props) {
    super(props);
    this.tileNodeRef = null; // this will be the ref of the main wrapping tile element
  }

  componentDidMount() {
    this.doPostRender();
  }
  
  componentDidUpdate() {
    this.doPostRender();
  }
  
  doPostRender() {
    bindTextLinkClickTrackingEvents(this.tileNodeRef,  '.tile-caption a, .tile-text a', 'gridtilelink');
    setTextLinksToOpenInNewPage(this.tileNodeRef, '.tile-caption a, .tile-text a');
  }

  shouldTileBeInteractive() {
    const {itemType} = this.props;
    return itemType === 'form';
  }

  render() {
    const {
      actionData,
      altText,
      actionTypeName,
      asset,
      children,
      croppedImageUrl,
      doesTileLinkToVideo,
      embeddedIframeUrl,
      embeddedIframeTitle,
      fontSize,
      hasVideoIcon,
      horizontalAlign,
      htmlContent,
      innerTileStyle,
      itemType,
      linkDescription,
      linkHasNoImage,
      linkImageUrl,
      linkSource,
      linkTitle,
      pageId,
      captionData,
      shouldForceShowCaption,
      shouldCaptionBeUnderneath,
      shouldHideTileCaption,
      shouldShowExternalOpeningMsg,
      tileLinkHref,
      tileLinkTarget,
      tileActionIndicatorType,
      tile_inner_zf_image,
      uncroppedImageUrl,
      uuid,
      verticalAlign,
      videoPlayIcon,
      GridTileCaptionComponent,
      onLinkToVideoClick,
      onOverlayOpen,
      onVideoPlayButtonClick,
      animationSettings
    } = this.props;

    const contentItemType = itemType;    

    const shouldRenderTileCaption = (
      shouldForceShowCaption ||
      (
        includes(['html_package', 'iframe_embed'], contentItemType) ?
          !actionData && !!htmlContent
        : !includes(['link','form','text'], contentItemType) ?
          !!htmlContent
        :
        false
      )
    );

    // In mobile layout for certain sizes for exmaple, maybe render the caption *underneath* the tile;
    // this prevents the tile caption from completely covering the rest of the tile
    const shouldRenderCaptionUnderneath = (
      !shouldHideTileCaption &&
      shouldRenderTileCaption &&
      shouldCaptionBeUnderneath
    );

    var VideoPlayIconComponent = <i className="zfe-icon zfe-icon-play"></i>;

    if(videoPlayIcon){
      if(videoPlayIcon.setting == VIDEO_INLINE_SETTING_NONE){
        VideoPlayIconComponent = null;
      }
      if(videoPlayIcon.setting  == VIDEO_INLINE_SETTING_CUSTOM){
        VideoPlayIconComponent = (
          <Image 
            style={{ width: '100%', height: '100%' }} 
            zfImage={videoPlayIcon.zfImage} 
            altText={altText}
            src={videoPlayIcon.display_image} 
          />
        );
      }
    }

    return (
      <Fragment>
        <GridTileLinkTapTarget
          doesOpenOverlay={actionTypeName === 'overlay'}
          tileLinkHref={tileLinkHref}
          tileLinkTarget={tileLinkTarget}
          tileActionIndicatorType={tileActionIndicatorType}
          captionHtml={!shouldHideTileCaption && shouldRenderTileCaption ? htmlContent : null}
          shouldShowExternalOpeningMsg={shouldShowExternalOpeningMsg}
          doesTileLinkToVideo={doesTileLinkToVideo}
          onOverlayOpen={onOverlayOpen}
          onLinkToVideoClick={onLinkToVideoClick}
        />

        {
          hasVideoIcon &&
            <div onClick={onVideoPlayButtonClick} key="video-play-button" className="video-play-button">
              <div className="video-play-button-icon-wrapper">
                { VideoPlayIconComponent }
              </div>
            </div>
        }

        <div 
          ref={(ref)=> { this.tileNodeRef = ref; }}
          key="tile"
          tabIndex={this.shouldTileBeInteractive() ? "0" : undefined} 
          className="tile" 
          style={{
            fontSize: `${fontSize || 100}%`,
            ...innerTileStyle
          }}
        >
          {
            children ?

              children

            : contentItemType == 'text' ?

              <GridTileInnerTypeText 
                htmlContent={htmlContent}
                horizontalAlign={horizontalAlign}
                verticalAlign={verticalAlign}
                animationSettings={animationSettings}
              />
            
            : contentItemType == 'link' ?

              <GridTileInnerTypeWebPreview 
                zfImage={tile_inner_zf_image}
                altText={altText}
                linkImageUrl={linkImageUrl}
                linkTitle={linkTitle || ''}
                linkSource={linkSource || ''}
                linkDescription={linkDescription || ''}
                linkHasNoImage={linkHasNoImage}
                horizontalAlign={horizontalAlign}
                innerTileStyle={innerTileStyle}
              />

            : contentItemType == 'form' ?

              <GridTileInnerTypeFormContainer 
                contentUuid={uuid}
                headerHtml={htmlContent}
                asset={asset}
                pageId={pageId}
                horizontalAlign={horizontalAlign}
                verticalAlign={verticalAlign}
              />

            : (
              get(actionData, 'type') === 'none' &&
              includes(['html_package', 'iframe_embed'], contentItemType)
            ) ?
              <GridTileInnerTypeIframe iframeSrc={embeddedIframeUrl} iframeTitle={embeddedIframeTitle || 'Embedded Content'} />
            :
              <GridTileInnerTypeImage
                zfImage={tile_inner_zf_image}
                altText={altText}
                needsCrop={croppedImageUrl === null}
                imageSrc={croppedImageUrl || uncroppedImageUrl}
                animationSettings={animationSettings}
              />

          }

          {
            (!shouldHideTileCaption && shouldRenderTileCaption && !shouldRenderCaptionUnderneath) &&
              <MemoizedCaptionComponent 
                key="caption-inside"
                contentUuid={uuid}
                captionData={captionData}
                captionHtml={htmlContent} 
                horizontalAlign={horizontalAlign} 
                GridTileCaptionComponent={GridTileCaptionComponent}
                innerTileStyle={innerTileStyle}
              />
          }
        </div>
            
        { 
          shouldRenderCaptionUnderneath &&
            <MemoizedCaptionComponent 
              key="caption-underneath"
              captionData={captionData}
              contentUuid={uuid}
              captionHtml={htmlContent} 
              horizontalAlign={horizontalAlign}
              GridTileCaptionComponent={GridTileCaptionComponent}
              innerTileStyle={innerTileStyle}
            />
        }
              
      </Fragment>
    );
  }
}

GridTile.defaultProps = {
  shouldCaptionBeUnderneath: false,
  shouldHideTileCaption: false,
  onVideoPlayButtonClick: noop
};

GridTile.propTypes = {
  uuid: PropTypes.number.isRequired,
  contentId: PropTypes.number.isRequired,
  pageId: PropTypes.number.isRequired,
  actionTypeName: PropTypes.string.isRequired,
  tileActionIndicatorType: PropTypes.string,
  actionData: PropTypes.any,
  asset: PropTypes.any,
  horizontalAlign: PropTypes.string,
  verticalAlign: PropTypes.string,
  htmlContent: PropTypes.string,
  embeddedIframeUrl: PropTypes.string,
  innerTileStyle: PropTypes.object.isRequired,
  fontSize: PropTypes.number,
  stylePresetClassName: PropTypes.string,
  cssClassesList: PropTypes.array,
  linkTitle: PropTypes.string,
  linkSource: PropTypes.string,
  linkDescription: PropTypes.string,
  hasVideoIcon: PropTypes.bool.isRequired,
  itemType: PropTypes.string.isRequired,
  shouldCaptionBeUnderneath: PropTypes.bool,
};

export default GridTile;
